import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "antd";
import {
  Sparkle,
  ArrowsClockwise,
  WarningCircle,
  Clock,
} from "@phosphor-icons/react";
import {
  fetchContextAnalysis,
  refreshContextAnalysis,
  selectContextAnalysisForDocument,
  selectIsContextAnalysisRunning,
} from "../../slice/AISlice";
import usePollContextAnalysis from "../../hooks/usePollContextAnalysis";
import BriefingMarkdown from "./BriefingMarkdown";
import ContextAnalysisFeedbackBar from "./ContextAnalysisFeedbackBar";
import { handleCitationClick } from "../../utils/citationJump";
import DocumentPreviewModal from "../Categorization/GoldStandardViewerModal";
import {
  AIPaneScrollBody,
  AnalysisEmptyDescription,
  AnalysisEmptyIconRing,
  AnalysisEmptyTitle,
  AnalysisEmptyWrapper,
  AnalysisErrorBanner,
  AnalysisStatusPill,
  AnalysisStatusPillDot,
  BriefingTimestamp,
  BriefingDivider,
  BriefingSkeletonBlock,
  BriefingSkeletonGroup,
  ContextAnalysisHeader,
  ContextAnalysisRefreshButton,
  ContextAnalysisStaleMarker,
  RecommendedActionsSection,
  RecommendedActionsHeader,
  RecommendedActionItem,
  RecommendedActionTitle,
  RecommendedActionUrgency,
  RecommendedActionRationale,
} from "./AIPaneStyled";

/**
 * Phase 5 — per-document Context Analysis tab.
 *
 * Renders the BE-produced "fit into matter" markdown narrative + (eventually)
 * a list of recommended actions. Currently the backend ships a deterministic
 * scaffold paragraph and an empty `recommended_actions` array; the component
 * is designed to light up automatically when the LLM swap lands without
 * needing a parallel FE change.
 *
 * State machine (BE `data.status`):
 *   not_started — no row exists, so nothing is enqueued. Say so and
 *                offer "Run analysis"; keep polling so a producer run
 *                started elsewhere (e.g. the pipeline trigger) is
 *                picked up without a reload.
 *   pending    — producer enqueued; show running stub + keep polling.
 *   running    — producer in flight; show running stub. If we have a
 *                stale `content_md` from a previous run, surface it with
 *                a "Re-running…" hint at the top so the user isn't
 *                staring at a blank pane during a refresh.
 *   completed  — markdown body + recommended actions + computed_at chip.
 *                Stale marker if computed_at is older than 7 days.
 *   failed     — error banner over the prior content (or a full empty-
 *                state error if there's no prior content). The header
 *                refresh button drives recovery — it re-enqueues the
 *                producer in place via `refreshContextAnalysis`.
 *
 * Polling
 * -------
 * `usePollContextAnalysis` is mounted unconditionally. It's a no-op when
 * `documentId` is missing or status is terminal, so the hook order stays
 * stable across renders.
 *
 * Mounting strategy
 * -----------------
 * Like the rest of the AI panel, AIPane keeps Briefing + Context
 * Analysis tab bodies mounted across switches (so polling state and
 * scroll position survive a tab toggle). The `idle` → fetch behavior
 * matches BriefingTab and AnalysisTab.
 */

const STALE_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const formatRelative = (isoString) => {
  if (!isoString) return null;
  const ts = Date.parse(isoString);
  if (Number.isNaN(ts)) return null;
  const deltaMs = Date.now() - ts;
  if (deltaMs < 0) return "just now";
  const minutes = Math.floor(deltaMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(ts).toISOString().slice(0, 10);
};

const isStale = (isoString) => {
  if (!isoString) return false;
  const ts = Date.parse(isoString);
  if (Number.isNaN(ts)) return false;
  return Date.now() - ts > STALE_THRESHOLD_MS;
};

const STATUS_PILL_LABEL = {
  not_started: "Not started",
  pending: "Pending",
  running: "Running",
  completed: "Completed",
  failed: "Failed",
};

const ContextAnalysisTab = ({ documentId = null, isActive = true }) => {
  const dispatch = useDispatch();
  const slot = useSelector((state) =>
    selectContextAnalysisForDocument(state, documentId)
  );
  const isRunning = useSelector((state) =>
    selectIsContextAnalysisRunning(state, documentId)
  );

  // Polling lives at the tab level so it keeps running while the user
  // is on the Briefing tab (AIPane keeps both bodies mounted) — same
  // pattern as AnalysisTab + usePollAnalysisJob.
  usePollContextAnalysis({ documentId });

  // Initial-fetch dedupe. The polling hook fires its own immediate
  // poll, so we don't strictly need a separate `idle → fetch` here —
  // but mirroring BriefingTab's pattern keeps the slice status fresh
  // even on the failed → "user navigates back" retry path.
  const lastFetchedDocIdRef = useRef(null);
  useEffect(() => {
    if (!documentId || !isActive) return;
    if (slot.status === "idle") {
      lastFetchedDocIdRef.current = documentId;
      dispatch(fetchContextAnalysis(documentId));
      return;
    }
    if (slot.status === "failed" && lastFetchedDocIdRef.current !== documentId) {
      lastFetchedDocIdRef.current = documentId;
      dispatch(fetchContextAnalysis(documentId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, documentId, isActive]);

  const handleRetry = useCallback(() => {
    if (!documentId) return;
    lastFetchedDocIdRef.current = documentId;
    dispatch(fetchContextAnalysis(documentId));
  }, [dispatch, documentId]);

  /**
   * Manual refresh — re-runs the BE producer in place. The thunk
   * sets status back to `pending`, the polling hook then picks up
   * the new run and transitions through running → completed
   * automatically. Disabled while a run is already in flight (the
   * stale-marker / "Re-running…" affordance below already covers
   * that signal).
   */
  const handleRefresh = useCallback(() => {
    if (!documentId || isRunning) return;
    dispatch(refreshContextAnalysis(documentId));
  }, [dispatch, documentId, isRunning]);

  // Quick-look preview for ``dms-doc://`` evidence links in the
  // narrative (e.g. "read the June 11 DBQ" citations). Opens the
  // shared read-only Apryse modal so the user can verify the source
  // without leaving the inbox. Citations carrying the page/quote
  // deep-link params (BE PR #1794) open at the cited page with the
  // quote highlighted.
  const [docPreview, setDocPreview] = useState(null);
  const handleDocumentLinkClick = useCallback(
    (linkedDocumentId, label, _event, deepLink) => {
      if (!linkedDocumentId) return;
      setDocPreview({
        documentId: linkedDocumentId,
        filename: label || "",
        page: deepLink?.page ?? null,
        q: deepLink?.q || "",
      });
    },
    []
  );
  const closeDocPreview = useCallback(() => setDocPreview(null), []);

  // AIPane keeps this tab mounted across inbox document switches, so
  // an open preview must not survive into the next document's analysis.
  useEffect(() => {
    setDocPreview(null);
  }, [documentId]);

  // 1. No document selected — empty state shared with the matter-mode
  //    Briefing/Analysis tabs.
  if (!documentId) {
    return (
      <AnalysisEmptyWrapper>
        <AnalysisEmptyIconRing>
          <Sparkle size={32} weight="duotone" />
        </AnalysisEmptyIconRing>
        <AnalysisEmptyTitle>No document selected</AnalysisEmptyTitle>
        <AnalysisEmptyDescription>
          Pick a document from the inbox to see how it fits into its matter.
        </AnalysisEmptyDescription>
      </AnalysisEmptyWrapper>
    );
  }

  // 2. Initial-fetch skeleton. We show this only when both the slice
  //    status is loading AND we have no cached data yet — once the
  //    poll has returned even a `pending` row we transition to the
  //    real running stub below.
  if (
    (slot.status === "loading" || slot.status === "idle") &&
    !slot.data
  ) {
    return (
      <AIPaneScrollBody>
        <BriefingSkeletonGroup>
          <BriefingSkeletonBlock $w="35%" $h={20} />
          <BriefingSkeletonBlock $w="100%" $h={14} />
          <BriefingSkeletonBlock $w="92%" $h={14} />
          <BriefingSkeletonBlock $w="78%" $h={14} />
          <BriefingSkeletonBlock $w="60%" $h={20} />
          <BriefingSkeletonBlock $w="100%" $h={14} />
          <BriefingSkeletonBlock $w="88%" $h={14} />
        </BriefingSkeletonGroup>
      </AIPaneScrollBody>
    );
  }

  // 3. Initial fetch failed (and we don't have a previously-loaded
  //    payload to fall back to). Surface error + retry. Once Phase 5b
  //    lands the refresh button on the action footer, a hard "rerun"
  //    is also one click away — until then, retry-the-fetch is the
  //    only escape hatch.
  if (slot.status === "failed" && !slot.data) {
    return (
      <AnalysisEmptyWrapper>
        <AnalysisEmptyIconRing>
          <WarningCircle size={32} weight="duotone" />
        </AnalysisEmptyIconRing>
        <AnalysisEmptyTitle>Couldn&apos;t load context analysis</AnalysisEmptyTitle>
        <AnalysisEmptyDescription>
          {slot.error ||
            "Something went wrong loading this document's context analysis."}
        </AnalysisEmptyDescription>
        <Button
          icon={<ArrowsClockwise size={14} weight="bold" />}
          onClick={handleRetry}
        >
          Retry
        </Button>
      </AnalysisEmptyWrapper>
    );
  }

  const data = slot.data || {};
  // Absent status means we know of no run, which is `not_started` — never
  // claim `pending`, or the panel is a dead end again.
  const beStatus = data.status || "not_started";
  const contentMd = data.content_md || "";
  const recommendedActions = Array.isArray(data.recommended_actions)
    ? data.recommended_actions
    : [];
  const computedAt = data.computed_at || null;
  const errorMessage = data.error_message || "";

  // 4a. Nothing has ever been enqueued for this document. Distinct from
  //     a run in flight: say so plainly and offer the one control that
  //     actually helps. Previously the BE reported this as `pending`, so
  //     it rendered the in-flight stub below with no refresh affordance
  //     and no way out.
  if (beStatus === "not_started" && !contentMd) {
    return (
      <AIPaneScrollBody>
        <ContextAnalysisHeader>
          <AnalysisStatusPill $status={beStatus}>
            <AnalysisStatusPillDot />
            {STATUS_PILL_LABEL.not_started}
          </AnalysisStatusPill>
        </ContextAnalysisHeader>
        <AnalysisEmptyWrapper>
          <AnalysisEmptyIconRing>
            <Sparkle size={32} weight="duotone" />
          </AnalysisEmptyIconRing>
          <AnalysisEmptyTitle>Analysis hasn&apos;t run yet</AnalysisEmptyTitle>
          <AnalysisEmptyDescription>
            No matter-aware analysis has been produced for this document.
            Run it to see how it fits the matter.
          </AnalysisEmptyDescription>
          <Button
            type="primary"
            icon={<Sparkle size={14} weight="bold" />}
            onClick={handleRefresh}
            disabled={isRunning}
          >
            {isRunning ? "Starting…" : "Run analysis"}
          </Button>
        </AnalysisEmptyWrapper>
      </AIPaneScrollBody>
    );
  }

  // 4b. Producer running, no narrative to show yet. Calmer than a
  //     spinner-only screen — explains what's happening so the user
  //     isn't blocked. The refresh control stays reachable (disabled
  //     while in flight) so this state is never a dead end either.
  const isRunningWithoutContent =
    (beStatus === "pending" || beStatus === "running") && !contentMd;

  if (isRunningWithoutContent) {
    return (
      <AIPaneScrollBody>
        <ContextAnalysisHeader>
          <AnalysisStatusPill $status={beStatus}>
            <AnalysisStatusPillDot />
            {STATUS_PILL_LABEL[beStatus] || "Analyzing"}
          </AnalysisStatusPill>
          <ContextAnalysisRefreshButton
            type="button"
            onClick={handleRefresh}
            disabled={isRunning}
            aria-label="Re-run context analysis"
            title={
              isRunning
                ? "Re-running…"
                : "Re-run the matter-aware analysis with the latest matter context."
            }
          >
            <ArrowsClockwise size={14} weight="bold" />
            {isRunning ? "Running…" : "Refresh"}
          </ContextAnalysisRefreshButton>
        </ContextAnalysisHeader>
        <AnalysisEmptyWrapper>
          <AnalysisEmptyIconRing>
            <Sparkle size={32} weight="duotone" />
          </AnalysisEmptyIconRing>
          <AnalysisEmptyTitle>
            Analyzing how this fits the matter…
          </AnalysisEmptyTitle>
          <AnalysisEmptyDescription>
            The AI is correlating this document against the matter&apos;s
            existing evidence and condition history. Usually completes
            in 10–30 seconds.
          </AnalysisEmptyDescription>
        </AnalysisEmptyWrapper>
      </AIPaneScrollBody>
    );
  }

  // 5. Failed mid-run AFTER we've successfully shown content before.
  //    Preserve the prior content (the slice's rejected reducer
  //    already keeps `data` intact) and show a non-blocking error
  //    banner above it, so the user can still read the last good
  //    output. Phase 5b's refresh button drives the recovery path.
  const showStaleChip = beStatus === "completed" && isStale(computedAt);
  const showRunningChipOverPriorContent =
    (beStatus === "pending" || beStatus === "running") && contentMd;

  return (
    <AIPaneScrollBody>
      <ContextAnalysisHeader>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <AnalysisStatusPill $status={beStatus}>
            <AnalysisStatusPillDot />
            {STATUS_PILL_LABEL[beStatus] || "Pending"}
          </AnalysisStatusPill>
          {computedAt && (
            <BriefingTimestamp>
              Updated {formatRelative(computedAt)}
            </BriefingTimestamp>
          )}
        </div>
        {/* Refresh re-enqueues the producer in place. We render even
            during a running cycle (just disabled) so the affordance
            is discoverable — disabling vs. hiding keeps the layout
            stable and tells the user "yes, this exists; not now". */}
        <ContextAnalysisRefreshButton
          type="button"
          onClick={handleRefresh}
          disabled={isRunning}
          aria-label="Re-run context analysis"
          title={
            isRunning
              ? "Re-running…"
              : "Re-run the matter-aware analysis with the latest matter context."
          }
        >
          <ArrowsClockwise size={14} weight="bold" />
          {isRunning ? "Running…" : "Refresh"}
        </ContextAnalysisRefreshButton>
      </ContextAnalysisHeader>

      {beStatus === "failed" && errorMessage && (
        <AnalysisErrorBanner role="alert">{errorMessage}</AnalysisErrorBanner>
      )}

      {showRunningChipOverPriorContent && (
        <ContextAnalysisStaleMarker as="div" role="status">
          <Sparkle size={12} weight="fill" />
          Re-running — current content is from the previous run.
        </ContextAnalysisStaleMarker>
      )}

      {showStaleChip && (
        <ContextAnalysisStaleMarker role="note">
          <Clock size={12} weight="fill" />
          Last updated {formatRelative(computedAt)} — content may be stale.
        </ContextAnalysisStaleMarker>
      )}

      {contentMd ? (
        <BriefingMarkdown
          content={contentMd}
          onCitationClick={(parsed) => handleCitationClick(parsed, documentId)}
          onDocumentLinkClick={handleDocumentLinkClick}
        />
      ) : (
        <AnalysisEmptyWrapper>
          <AnalysisEmptyTitle>No context analysis yet</AnalysisEmptyTitle>
          <AnalysisEmptyDescription>
            The matter-aware analysis will appear here once the producer
            finishes running.
          </AnalysisEmptyDescription>
        </AnalysisEmptyWrapper>
      )}

      {recommendedActions.length > 0 && (
        <RecommendedActionsSection>
          <RecommendedActionsHeader>Recommended actions</RecommendedActionsHeader>
          {recommendedActions.map((action, idx) => {
            // The LLM producer emits ``severity`` (context-analysis
            // schema); the earlier scaffold contract said ``urgency``.
            // Accept both so neither payload shape loses its chip.
            const urgency = action?.urgency || action?.severity || null;
            return (
            <RecommendedActionItem
              key={action?.action || idx}
              $urgency={urgency || "medium"}
            >
              <RecommendedActionTitle>
                {action?.action || "Action"}
                {urgency && (
                  <RecommendedActionUrgency $urgency={urgency}>
                    {urgency}
                  </RecommendedActionUrgency>
                )}
              </RecommendedActionTitle>
              {action?.rationale && (
                <RecommendedActionRationale>
                  {action.rationale}
                </RecommendedActionRationale>
              )}
            </RecommendedActionItem>
            );
          })}
        </RecommendedActionsSection>
      )}

      {/* Visual divider — keeps the layout from looking truncated
          when the recommended-actions section is empty (the common
          case until the BE LLM swap lands). The refresh affordance
          itself lives in the header above, not here. */}
      {recommendedActions.length === 0 && contentMd && <BriefingDivider />}

      {/* Judgment capture on the completed run. The bar probes its own
          endpoint and self-hides when the backend 403s (capability
          gate, limited to Matthew) — so for everyone else this renders
          nothing. Only offered on a completed run: rating an analysis
          mid-rewrite would attach the comment to content the user
          can't see. */}
      {beStatus === "completed" && contentMd && (
        <ContextAnalysisFeedbackBar documentId={documentId} />
      )}

      <DocumentPreviewModal
        open={Boolean(docPreview)}
        onClose={closeDocPreview}
        documentId={docPreview?.documentId || null}
        filename={docPreview?.filename || ""}
        initialPage={docPreview?.page ?? null}
        highlightQuery={docPreview?.q || ""}
        fullHeight
      />
    </AIPaneScrollBody>
  );
};

ContextAnalysisTab.propTypes = {
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isActive: PropTypes.bool,
};

export default ContextAnalysisTab;
